import { RoomCapacity } from '@common/components/RoomCapacity';
import { useTranslations } from '@lang';
import rewriteHttp from '@utils/rewriteHttp';
import { FC } from 'react';
import { Link } from 'shared-routing';
import { BackgroundImgixWrapper, EntityImagePreview } from '@common/components';
import { HurryMessage } from '@common/components/HurryMessage';
import { PaymentTermsBadge } from '@common/components/PaymentTermsBadge';
import { DiscountBanner, DiscountPrice } from '@common/components/Price';
import { ListItemProps } from './list.types';
import TextTruncate from 'react-text-truncate';
import {
  BriefDescription,
  CallToActionWrapper,
  ItemAttributes,
  ItemCol,
  ItemDescription,
  ItemImageWrapper,
  ItemName,
  ItemPrice,
  ItemRow,
  ItemWrapper,
  MetadataRow,
  NoAvailabilityStyled,
  PaymentTermsRow,
  PriceWrapper,
  TitleRow,
  UnAvailableWrapper,
} from './ListItem.styles';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

export const ListItem: FC<ListItemProps> = ({
  renderMetadataRow,
  item,
  displayWithBriefDescription,
}) => {
  const {
    imageUrl,
    title,
    briefDescription,
    price,
    linkRoute,
    linkParams,
    displayUrgencyMessaging,
    quantity,
    unAvailable,
    maxOccupancy,
    maxAdults,
    maxChildren,
    isFixedPrice,
    discount,
    paymentTerms,
  } = item;

  const { t } = useTranslations();

  const showCTA = useFeatureIsOn('feature.listing-cta');
  const renderPrice = () => {
    if (!price) return null;

    return (
      <ItemCol>
        <ItemPrice>
          {isFixedPrice ? (
            <PriceWrapper isFixedPrice>
              <DiscountPrice price={price} discount={discount} />
            </PriceWrapper>
          ) : (
            t('common.priceIndicatorFrom', {
              price: (
                <PriceWrapper>
                  <DiscountPrice price={price} discount={discount} />
                </PriceWrapper>
              ),
            })
          )}
        </ItemPrice>
      </ItemCol>
    );
  };

  return (
    <ItemWrapper>
      <Link route={linkRoute} params={linkParams}>
        <ItemImageWrapper isTransparent={!!unAvailable}>
          <BackgroundImgixWrapper src={rewriteHttp(imageUrl)}>
            <DiscountBanner discount={discount} />
            <EntityImagePreview background={!!imageUrl} />
          </BackgroundImgixWrapper>
        </ItemImageWrapper>
      </Link>

      <ItemDescription>
        <TitleRow>
          <Link route={linkRoute} params={linkParams}>
            {renderMetadataRow && <MetadataRow>{renderMetadataRow(item)}</MetadataRow>}
            <ItemName title={title}>
              <TextTruncate line={3} truncateText="..." text={title} />
            </ItemName>
          </Link>

          {showCTA && (
            <Link
              route={linkRoute}
              params={linkParams}
              aria-label={t('common.view', { itemName: title })}
            >
              <CallToActionWrapper
                data-cy="list-item_cta"
                title={t('common.view', { itemName: title })}
              >
                {t('common.view.plain')}
              </CallToActionWrapper>
            </Link>
          )}
        </TitleRow>

        {displayWithBriefDescription && briefDescription && (
          <Link route={linkRoute} params={linkParams}>
            <BriefDescription title={briefDescription}>
              <TextTruncate line={3} truncateText="..." text={briefDescription} textElement="p" />
            </BriefDescription>
          </Link>
        )}

        {(maxOccupancy || maxAdults || maxChildren) && (
          <ItemAttributes>
            <RoomCapacity
              maxOccupancy={maxOccupancy}
              maxAdults={maxAdults}
              maxChildren={maxChildren}
            />
          </ItemAttributes>
        )}

        <ItemRow>
          {renderPrice()}
          {displayUrgencyMessaging && quantity && (
            <ItemCol>
              <HurryMessage quantity={quantity} small />
            </ItemCol>
          )}
        </ItemRow>

        {!unAvailable && paymentTerms && (
          <PaymentTermsRow>
            <PaymentTermsBadge paymentTerms={paymentTerms} />
          </PaymentTermsRow>
        )}

        {unAvailable && (
          <UnAvailableWrapper>
            <NoAvailabilityStyled>{t('list.noAvailability')}</NoAvailabilityStyled>
          </UnAvailableWrapper>
        )}
      </ItemDescription>
    </ItemWrapper>
  );
};
